import { ArrowUpRight, ChevronLeft } from 'lucide-react'
import type { SupportConversation } from '@manaro/contracts'
import { dayLabel } from '@/features/support-chat/chat-format.ts'
import { Avatar } from '@/shared/ui/avatar.tsx'
import { Badge } from '@/shared/ui/badge.tsx'
import { cn } from '@/shared/lib/cn.ts'
import { formatRelative, formatTime, plural } from '@/shared/lib/format.ts'

interface ConversationRowProps {
  item: SupportConversation
  active: boolean
  onSelect: () => void
}

const when = (iso: string): string => (dayLabel(iso) === 'Сегодня' ? formatTime(iso) : dayLabel(iso))

export function ConversationRow({ item, active, onSelect }: ConversationRowProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active || undefined}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors md:px-5',
        active ? 'bg-brand-tint' : 'active:bg-surface-hover md:hover:bg-[#fcfbf7]',
      )}
    >
      <Avatar name={item.author ?? item.title ?? '?'} className="size-12 text-[15px]" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5 border-b border-line-soft pb-3 [button:last-child_&]:border-b-0">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-[15.5px] font-medium tracking-[-0.005em]">
            {item.title ?? 'Без темы'}
          </span>
          {item.lastActivityAt && (
            <span className="tabular shrink-0 text-[12.5px] text-ink-muted">{when(item.lastActivityAt)}</span>
          )}
        </span>
        <span className="flex items-center gap-2 text-[13px] text-ink-muted">
          <span className="min-w-0 flex-1 truncate">
            {[item.author, `${item.messageCount} ${plural(item.messageCount, ['сообщение', 'сообщения', 'сообщений'])}`]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {item.escalated && <Badge tone="warn">Передано</Badge>}
        </span>
      </span>
    </button>
  )
}

export function ConversationHeader({ item, onBack }: { item: SupportConversation; onBack?: () => void }) {
  return (
    <div className="flex shrink-0 items-center gap-1 border-b border-line bg-surface px-1.5 pt-[env(safe-area-inset-top)] md:px-3">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Назад к обращениям"
          className="flex size-11 shrink-0 items-center justify-center rounded-full text-brand active:bg-surface-hover"
        >
          <ChevronLeft className="size-7" strokeWidth={2} />
        </button>
      )}
      <div className={cn('flex min-h-[56px] min-w-0 flex-1 flex-col justify-center py-2', !onBack && 'px-2')}>
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="min-w-0 truncate text-[16px] font-semibold tracking-[-0.01em]">{item.title ?? 'Без темы'}</h2>
          {item.escalated && (
            <Badge tone="warn" className="shrink-0">
              <ArrowUpRight className="size-3" aria-hidden />
              Передано
            </Badge>
          )}
        </div>
        <p className="truncate text-[12.5px] text-ink-muted">
          {[item.author, item.platform, item.startedAt ? `начато ${formatRelative(item.startedAt)}` : null]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
    </div>
  )
}
